import React, { useState, useEffect, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Modal from '../../components/Modal';
import SupplierController from './controllers/supplier-controller';
import ReglementFournisseurController from './controllers/reglement-fournisseur-controller';
import ForecastCalendarModal from './reglement-components/ForecastCalendarModal';
import { usePermissionViewModel } from './permissions/viewmodel/permission-viewmodel';

const fmt = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} TND`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const initials = (name = '') => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('') || '?';

// Deterministic gradient per supplier name for avatars.
const AVATAR_GRADIENTS = [
  'from-emerald-500 to-teal-600',
  'from-sky-500 to-indigo-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-pink-600',
  'from-violet-500 to-purple-600',
  'from-cyan-500 to-blue-600',
];
const avatarGradient = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 997;
  return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length];
};

const PAYMENT_MODES = {
  cash: { label: 'Espèce', color: 'bg-emerald-100 text-emerald-700', icon: '💵' },
  cheque: { label: 'Chèque', color: 'bg-blue-100 text-blue-700', icon: '🧾' },
  virement: { label: 'Virement', color: 'bg-violet-100 text-violet-700', icon: '🏦' },
  carte: { label: 'Carte', color: 'bg-amber-100 text-amber-700', icon: '💳' },
  traite: { label: 'Traite', color: 'bg-rose-100 text-rose-700', icon: '📜' },
};
const modeBadge = (m) => PAYMENT_MODES[m] || { label: m || '—', color: 'bg-gray-100 text-gray-600', icon: '•' };

const ReglementFournisseurPage = () => {
  const { hasPermission } = usePermissionViewModel('reglement_fournisseur');
  const canWrite = hasPermission('write', 'reglement_fournisseur');

  const [soldes, setSoldes] = useState([]);
  const [loadingSoldes, setLoadingSoldes] = useState(true);
  const [search, setSearch] = useState('');

  const [reglements, setReglements] = useState([]);
  const [loadingReglements, setLoadingReglements] = useState(true);
  const [page, setPage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterFournisseurId, setFilterFournisseurId] = useState('');
  const pageSize = 10;

  // Add-payment modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({
    fournisseurId: '',
    montant: '',
    modePaiement: 'cash',
    reference: '',
    commentaire: '',
    dateReglement: todayISO(),
  });
  const [selectedSolde, setSelectedSolde] = useState(null);
  const [loadingSolde, setLoadingSolde] = useState(false);

  // Payment forecast calendar
  const [forecastOpen, setForecastOpen] = useState(false);
  const [forecastSupplier, setForecastSupplier] = useState(null);

  const openForecast = (s) => {
    setForecastSupplier(s);
    setForecastOpen(true);
  };

  const loadSoldes = useCallback(async () => {
    setLoadingSoldes(true);
    try {
      const data = await ReglementFournisseurController.getAllSoldes();
      setSoldes(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error('Erreur de chargement des soldes fournisseurs');
    } finally {
      setLoadingSoldes(false);
    }
  }, []);

  const loadReglements = useCallback(async () => {
    setLoadingReglements(true);
    try {
      const data = await ReglementFournisseurController.fetchReglements(page, pageSize, {
        fournisseurId: filterFournisseurId || null,
      });
      setReglements(data.list || []);
      setMaxPage(data.maxPage || 1);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
      toast.error('Erreur de chargement des règlements');
    } finally {
      setLoadingReglements(false);
    }
  }, [page, filterFournisseurId]);

  useEffect(() => { loadSoldes(); }, [loadSoldes]);
  useEffect(() => { loadReglements(); }, [loadReglements]);

  const openModal = async () => {
    setForm({ fournisseurId: '', montant: '', modePaiement: 'cash', reference: '', commentaire: '', dateReglement: todayISO() });
    setSelectedSolde(null);
    setIsModalOpen(true);
    try {
      const list = await SupplierController.fetchAllSuppliers();
      setSuppliers(Array.isArray(list) ? list : (list?.list || []));
    } catch (e) {
      console.error(e);
      toast.error('Erreur de chargement des fournisseurs');
    }
  };

  // When a supplier is selected, fetch what we owe them.
  const handleSupplierChange = async (id) => {
    setForm((f) => ({ ...f, fournisseurId: id }));
    setSelectedSolde(null);
    if (!id) return;
    setLoadingSolde(true);
    try {
      const solde = await ReglementFournisseurController.getSolde(id);
      setSelectedSolde(solde);
    } catch (e) {
      console.error(e);
      toast.error('Erreur de calcul du solde');
    } finally {
      setLoadingSolde(false);
    }
  };

  // Open the add-payment modal pre-selected for a given supplier balance row.
  const openModalForSupplier = async (s) => {
    const id = s.fournisseurId ?? s.id;
    setForm({ fournisseurId: '', montant: '', modePaiement: 'cash', reference: '', commentaire: '', dateReglement: todayISO() });
    setSelectedSolde(null);
    setIsModalOpen(true);
    try {
      const list = await SupplierController.fetchAllSuppliers();
      setSuppliers(Array.isArray(list) ? list : (list?.list || []));
    } catch (e) {
      console.error(e);
      toast.error('Erreur de chargement des fournisseurs');
    }
    if (id) handleSupplierChange(String(id));
  };

  const handleSubmit = async () => {
    if (!form.fournisseurId) { toast.error('Sélectionnez un fournisseur'); return; }
    const montant = parseFloat(form.montant);
    if (!Number.isFinite(montant) || montant <= 0) { toast.error('Montant invalide'); return; }
    setSubmitting(true);
    try {
      await ReglementFournisseurController.createReglement({
        fournisseurId: Number(form.fournisseurId),
        montant,
        modePaiement: form.modePaiement,
        reference: form.reference || null,
        commentaire: form.commentaire || null,
        dateReglement: form.dateReglement ? new Date(form.dateReglement).toISOString() : null,
      });
      toast.success('Règlement enregistré');
      setIsModalOpen(false);
      loadSoldes();
      loadReglements();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce règlement ?')) return;
    try {
      await ReglementFournisseurController.deleteReglement(id);
      toast.success('Règlement supprimé');
      loadSoldes();
      loadReglements();
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredSoldes = soldes.filter((s) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (s.fournisseur?.name || '').toLowerCase().includes(term);
  });

  const totalDu = soldes.reduce((sum, s) => sum + (s.solde > 0 ? s.solde : 0), 0);
  const totalAchats = soldes.reduce((s, x) => s + (x.totalAchats || 0), 0);
  const totalRegle = soldes.reduce((s, x) => s + (x.totalReglements || 0), 0);
  const nbFournisseursDus = soldes.filter((s) => s.solde > 0).length;
  const payRatio = totalAchats > 0 ? Math.min(100, Math.round((totalRegle / totalAchats) * 100)) : 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Hero header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#174540] via-[#123735] to-[#0d2926] p-6 sm:p-8 mb-6 shadow-xl">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-emerald-500/10 blur-2xl"></div>
        <div className="absolute -bottom-16 -left-8 w-56 h-56 rounded-full bg-teal-400/10 blur-3xl"></div>
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-emerald-300 text-xs font-semibold tracking-wide mb-3">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              MODULE FINANCIER
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Règlement Fournisseur</h1>
            <p className="text-sm text-emerald-100/70 mt-1">Suivi des montants dus et paiements aux fournisseurs</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { loadSoldes(); loadReglements(); }}
              className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
              title="Rafraîchir"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" /></svg>
            </button>
            {canWrite && (
              <button
                onClick={openModal}
                className="inline-flex items-center px-5 py-3 rounded-xl text-sm font-semibold text-[#174540] bg-white hover:bg-emerald-50 shadow-lg transition-all hover:scale-[1.02]"
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Ajouter un règlement
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="relative overflow-hidden bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">Total dû</span>
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-red-50 text-red-600">
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" /><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" /></svg>
            </span>
          </div>
          <div className="text-2xl font-bold text-red-600 mt-2">{fmt(totalDu)}</div>
          <div className="text-xs text-gray-400 mt-1">{nbFournisseursDus} fournisseur(s) à régler</div>
        </div>

        <div className="relative overflow-hidden bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">Total achats</span>
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gray-100 text-gray-700">
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" /></svg>
            </span>
          </div>
          <div className="text-2xl font-bold text-gray-900 mt-2">{fmt(totalAchats)}</div>
          <div className="text-xs text-gray-400 mt-1">Cumul des bons d'achat</div>
        </div>

        <div className="relative overflow-hidden bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">Total réglé</span>
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            </span>
          </div>
          <div className="text-2xl font-bold text-emerald-700 mt-2">{fmt(totalRegle)}</div>
          <div className="text-xs text-gray-400 mt-1">Paiements effectués</div>
        </div>

        <div className="relative overflow-hidden bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">Taux de règlement</span>
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" /></svg>
            </span>
          </div>
          <div className="text-2xl font-bold text-blue-700 mt-2">{payRatio}%</div>
          <div className="mt-2 h-2 w-full rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-700" style={{ width: `${payRatio}%` }}></div>
          </div>
        </div>
      </div>

      {/* Balances per supplier */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-8">
        <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#174540]/10 text-[#174540]">
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" /></svg>
            </span>
            <h2 className="text-lg font-semibold text-gray-900">Soldes par fournisseur</h2>
          </div>
          <div className="relative w-72 max-w-full">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" /></svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un fournisseur…"
              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-[#174540] focus:outline-none transition-colors"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50/70">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Fournisseur</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Total achats</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Total réglé</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Progression</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Reste à payer</th>
                <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loadingSoldes ? (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-gray-400">Chargement…</td></tr>
              ) : filteredSoldes.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-gray-400">Aucun fournisseur.</td></tr>
              ) : filteredSoldes.map((s) => {
                const name = s.fournisseur?.name || `#${s.fournisseurId}`;
                const ratio = s.totalAchats > 0 ? Math.min(100, Math.round((s.totalReglements / s.totalAchats) * 100)) : 0;
                return (
                  <tr key={s.fournisseurId} className="hover:bg-gray-50/70 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`flex-shrink-0 h-10 w-10 rounded-xl bg-gradient-to-br ${avatarGradient(name)} text-white flex items-center justify-center text-sm font-bold shadow-sm`}>
                          {initials(name)}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{name}</div>
                          <div className="text-xs text-gray-400">{s.nbAchats} achat(s) • {s.nbReglements} règlement(s)</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right text-gray-700 font-medium">{fmt(s.totalAchats)}</td>
                    <td className="px-5 py-4 text-right text-emerald-700 font-medium">{fmt(s.totalReglements)}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2 min-w-[120px]">
                        <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div className={`h-full rounded-full ${ratio >= 100 ? 'bg-emerald-500' : 'bg-gradient-to-r from-amber-400 to-emerald-500'}`} style={{ width: `${ratio}%` }}></div>
                        </div>
                        <span className="text-xs font-medium text-gray-500 w-9 text-right">{ratio}%</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <span className={`inline-flex px-2.5 py-1 rounded-lg text-sm font-bold ${s.solde > 0 ? 'bg-red-50 text-red-600' : s.solde < 0 ? 'bg-amber-50 text-amber-600' : 'bg-gray-50 text-gray-400'}`}>
                        {fmt(s.solde)}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => { setFilterFournisseurId(String(s.fournisseurId)); setPage(1); document.getElementById('history-section')?.scrollIntoView({ behavior: 'smooth' }); }}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-[#174540] bg-[#174540]/5 hover:bg-[#174540]/10 transition-colors"
                          title="Voir les règlements"
                        >
                          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
                          Règlements
                        </button>
                        <button
                          onClick={() => openForecast(s)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors"
                          title="Prévision de paiement"
                        >
                          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" /></svg>
                          Prévision
                        </button>
                        {canWrite && (
                          <button
                            onClick={() => openModalForSupplier(s)}
                            className="inline-flex items-center justify-center h-7 w-7 rounded-lg text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                            title="Régler ce fournisseur"
                          >
                            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payments history */}
      <div id="history-section" className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600">
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>
            </span>
            <h2 className="text-lg font-semibold text-gray-900">Historique des règlements</h2>
            <span className="ml-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs font-medium">{total}</span>
          </div>
          <div className="flex items-center gap-2">
            {filterFournisseurId && (
              <button
                onClick={() => { setFilterFournisseurId(''); setPage(1); }}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                Filtre
              </button>
            )}
            <select
              value={filterFournisseurId}
              onChange={(e) => { setFilterFournisseurId(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-[#174540] focus:outline-none transition-colors"
            >
              <option value="">Tous les fournisseurs</option>
              {soldes.map((s) => (
                <option key={s.fournisseurId} value={s.fournisseurId}>{s.fournisseur?.name || `#${s.fournisseurId}`}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50/70">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Fournisseur</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Mode</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Référence</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Montant</th>
                {canWrite && <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loadingReglements ? (
                <tr><td colSpan={canWrite ? 6 : 5} className="px-5 py-12 text-center text-gray-400">Chargement…</td></tr>
              ) : reglements.length === 0 ? (
                <tr><td colSpan={canWrite ? 6 : 5} className="px-5 py-16 text-center">
                  <div className="flex flex-col items-center gap-2 text-gray-400">
                    <svg className="h-12 w-12 text-gray-200" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" /></svg>
                    <span className="text-sm">Aucun règlement enregistré.</span>
                  </div>
                </td></tr>
              ) : reglements.map((r) => {
                const name = r.fournisseur?.name || `#${r.fournisseurId}`;
                const mode = modeBadge(r.modePaiement);
                return (
                  <tr key={r.id} className="hover:bg-gray-50/70 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2 text-gray-700">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gray-50 text-gray-400">
                          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" /></svg>
                        </span>
                        <span className="text-sm font-medium">{r.dateReglement ? new Date(r.dateReglement).toLocaleDateString('fr-FR') : '-'}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2.5">
                        <div className={`flex-shrink-0 h-8 w-8 rounded-lg bg-gradient-to-br ${avatarGradient(name)} text-white flex items-center justify-center text-xs font-bold`}>
                          {initials(name)}
                        </div>
                        <span className="text-gray-900 font-medium">{name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${mode.color}`}>
                        <span>{mode.icon}</span>{mode.label}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-gray-500 text-sm">{r.reference || '—'}</td>
                    <td className="px-5 py-4 text-right">
                      <span className="text-base font-bold text-emerald-700">{fmt(r.montant)}</span>
                    </td>
                    {canWrite && (
                      <td className="px-5 py-4 text-center">
                        <button
                          onClick={() => handleDelete(r.id)}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-red-500 bg-red-50 hover:bg-red-100 transition-colors"
                          title="Supprimer"
                        >
                          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {maxPage > 1 && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 bg-gray-50/50">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-xl border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
              Précédent
            </button>
            <span className="text-sm text-gray-500">Page <span className="font-semibold text-gray-900">{page}</span> / {maxPage}</span>
            <button disabled={page >= maxPage} onClick={() => setPage((p) => Math.min(maxPage, p + 1))} className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-xl border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              Suivant
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
            </button>
          </div>
        )}
      </div>

      {/* Add payment modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Ajouter un règlement"
        size="lg"
        onSubmit={handleSubmit}
        submitLabel="Enregistrer le règlement"
        isSubmitting={submitting}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fournisseur *</label>
            <select
              value={form.fournisseurId}
              onChange={(e) => handleSupplierChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#174540] focus:outline-none"
            >
              <option value="">Sélectionner…</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Balance preview */}
          {form.fournisseurId && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              {loadingSolde ? (
                <div className="text-sm text-gray-500">Calcul du solde…</div>
              ) : selectedSolde ? (
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Total achats</span><span className="font-medium">{fmt(selectedSolde.totalAchats)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Déjà réglé</span><span className="font-medium text-emerald-700">{fmt(selectedSolde.totalReglements)}</span></div>
                  <div className="flex justify-between pt-2 mt-1 border-t border-gray-300">
                    <span className="font-semibold text-gray-700">Reste à payer</span>
                    <span className={`font-bold ${selectedSolde.solde > 0 ? 'text-red-600' : 'text-gray-500'}`}>{fmt(selectedSolde.solde)}</span>
                  </div>
                  {selectedSolde.solde > 0 && (
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, montant: String(selectedSolde.solde) }))}
                      className="mt-2 text-xs font-medium text-[#174540] hover:underline"
                    >
                      Régler le montant total ({fmt(selectedSolde.solde)})
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Montant à payer *</label>
              <input
                type="number"
                step="0.001"
                min="0"
                value={form.montant}
                onChange={(e) => setForm((f) => ({ ...f, montant: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#174540] focus:outline-none"
                placeholder="0.000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={form.dateReglement}
                onChange={(e) => setForm((f) => ({ ...f, dateReglement: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#174540] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
              <select
                value={form.modePaiement}
                onChange={(e) => setForm((f) => ({ ...f, modePaiement: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#174540] focus:outline-none"
              >
                <option value="cash">Espèce</option>
                <option value="cheque">Chèque</option>
                <option value="virement">Virement</option>
                <option value="carte">Carte</option>
                <option value="traite">Traite</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Référence</label>
              <input
                type="text"
                value={form.reference}
                onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#174540] focus:outline-none"
                placeholder="N° chèque, virement…"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Commentaire</label>
            <textarea
              value={form.commentaire}
              onChange={(e) => setForm((f) => ({ ...f, commentaire: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#174540] focus:outline-none"
            />
          </div>
        </div>
      </Modal>

      <ForecastCalendarModal
        isOpen={forecastOpen}
        onClose={() => setForecastOpen(false)}
        supplier={forecastSupplier}
      />
    </div>
  );
};

export default ReglementFournisseurPage;
